"use client";

import * as React from "react";
import { Label, Pie, PieChart } from "recharts";

import { Card, CardContent, CardFooter } from "@/components/ui/card";
import {
    ChartConfig,
    ChartContainer,
    // ChartTooltip,
    // ChartTooltipContent,
} from "@/components/ui/chart";
import Bullet from "@/app/assets/icons/bullet";
import BulletBlur from "@/app/assets/icons/bullet-blur";
import { formatIndianCurrency } from "@/app/utils/helper";

interface DonutChartProps {
    totalInvestment: number;
    interestEarned: number;
    renderHeader: boolean;
}

const DonutChart: React.FC<DonutChartProps> = ({interestEarned, totalInvestment, renderHeader}) => {
    const chartData = [
        { name: "Invested", value: totalInvestment, fill: "#233CBD" },
        { name: "Earned", value: interestEarned, fill: "#99A9F7" },
    ];

    const chartConfig = {
        Invested: {
            label: "Invested",
            color: "#233CBD",
        },
        Earned: {
            label: "Earned",
            color: "#99A9F7",
        },
    } satisfies ChartConfig;

    const totalAmount = totalInvestment + interestEarned;

    return (
        <Card className="flex flex-col border-none bg-transparent p-0 shadow-none max-h-[342px] w-full md:px-6">
            <CardContent className="flex-1 p-0 md:p-0">
                <ChartContainer config={chartConfig} className="mx-auto aspect-square max-h-[200px]">
                    <PieChart width={200} height={200}>
                        {/* <ChartTooltip cursor={false} content={<ChartTooltipContent hideLabel />} /> */}
                        <Pie
                            data={chartData}
                            dataKey="value"
                            nameKey="name"
                            innerRadius={90}
                            outerRadius={100}
                            strokeWidth={5}
                        >
                            <Label
                                content={({ viewBox }) => {
                                    if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                                        return (
                                            <text
                                                x={viewBox.cx}
                                                y={viewBox.cy}
                                                textAnchor="middle"
                                                dominantBaseline="middle"
                                            >
                                                <tspan
                                                    x={viewBox.cx}
                                                    y={(viewBox.cy || 0) - 20}
                                                    className="fill-muted-foreground text-sm"
                                                >
                                                    You get
                                                </tspan>
                                                <tspan
                                                    x={viewBox.cx}
                                                    y={(viewBox.cy || 0) + 20}
                                                    className="fill-foreground text-2xl font-bold"
                                                >
                                                    {formatIndianCurrency(Number(totalAmount.toFixed(0)))}
                                                </tspan>
                                            </text>
                                        );
                                    }
                                }}
                            />
                        </Pie>
                    </PieChart>
                </ChartContainer>
            </CardContent>
            <CardFooter className="card-footer mx-4 md:mx-0 mt-6 flex items-center justify-center gap-1 border-t border-neutralOpacityDark-10 pt-6 px-3 md:px-0 pb-0 text-sm">
                <div className="flex flex-1 items-start gap-2">
                    <div className="pt-[1px]">
                        <Bullet />
                    </div>
                    <div className="flex flex-col">
                        <p className="text-left text-[10px] text-neutral-500 leading-[13px]">Invested</p>
                        <p className="text-left text-[13px] md:text-sm font-semibold leading-[22.4px]">{formatIndianCurrency(Number(totalInvestment.toFixed(0)))}</p>
                    </div>
                </div>
                <div className="flex flex-1 items-start gap-2">
                    <div className="pt-[1px]">
                        <BulletBlur />
                    </div>
                    <div className="flex flex-col">
                        <p className="text-left text-[10px] text-neutral-500 leading-[13px]">{!renderHeader ? 'Est. Returns' : 'Earned'}</p>
                        <p className="text-left text-[13px] md:text-sm font-semibold leading-[22.4px]">{formatIndianCurrency(Number(interestEarned.toFixed(0)))}</p>
                    </div>
                </div>
                <div className="flex flex-1 flex-col">
                    <div className="flex flex-col">
                        <p className="text-left text-[10px] text-neutral-500 leading-[13px]">{!renderHeader ? 'Total Value' : 'Maturity amount'}</p>
                        <p className="text-left text-[13px] md:text-sm font-semibold leading-[22.4px]">{formatIndianCurrency(Number(totalAmount.toFixed(0)))}</p>
                    </div>
                </div>
            </CardFooter>
        </Card>
    );
};

export default DonutChart;